import React, { useRef, useState, useEffect } from 'react';
import { Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { useSelector } from 'react-redux';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DatePicker, { DateRange } from './DatePicker';

interface TopbarProps {
  showGreeting?: boolean;
  placeholderText?: string;
  showPlaceholder?: boolean;
}

const startOfDay = (date: Date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const endOfDay = (date: Date) => {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
};

function Topbar({ showGreeting = true, placeholderText = 'Date', showPlaceholder = true }: TopbarProps) {
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: startOfDay(new Date()),
    endDate: endOfDay(new Date()),
  });
  
  const notificationRef = useRef<any>(null);
  const datePickerRef = useRef<any>(null);
  const [greeting, setGreeting] = useState('Hello');
  const user: any = useSelector((state: any) => state.auth?.user);
  const userName = user?.email === "monica@kimaru.ai" ? "Monica" : "Sam";

  const notifications = [
    {
      id: 1,
      icon: '/assets/icons/Info.svg',
      message: 'Your report has been generated',
      time: '2 hours ago',
      isRead: false,
    },
    {
      id: 2,
      icon: '/assets/icons/Check.svg',
      message: 'Decision tracker updated successfully',
      time: '5 hours ago',
      isRead: false,
    },
    {
      id: 3,
      icon: '/assets/icons/Alert.svg',
      message: 'New data available in library',
      time: '1 day ago',
      isRead: true,
    },
  ];

  // Handle greeting
  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) {
      setGreeting('Good Morning');
    } else if (hour < 18) {
      setGreeting('Good Afternoon');
    } else {
      setGreeting('Good Evening');
    }
  }, []);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setIsNotificationOpen(false);
      }
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setIsDatePickerOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleDateRangeApply = (range: DateRange) => {
    setDateRange(range);
  };

  const formatDateRange = (range: DateRange): string => {
    if (!range.startDate || !range.endDate) return placeholderText;
    
    const format = (date: Date) => {
      return `${date.getMonth() + 1}/${date.getDate()}`;
    };
    
    const start = range.startDate;
    const end = range.endDate;
    
    const startNormalized = startOfDay(start);
    const endNormalized = startOfDay(end);
    
    if (range.preset === 'yesterday') {
      return `Yesterday (${format(start)})`;
    }
    
    if (startNormalized.getTime() === endNormalized.getTime()) {
      return `Today (${format(start)})`;
    }
    if (range.preset === 'week-current') {
      return `Current Week (${format(start)} - ${format(end)})`;
    }
    if (range.preset === 'week-previous') {
      return `Previous Week (${format(start)} - ${format(end)})`;
    }
    if (range.preset === 'month-current') {
      return `Current Month (${format(start)} - ${format(end)})`;
    }
    if (range.preset === 'month-previous') {
      return `Previous Month (${format(start)} - ${format(end)})`;
    }
    if (range.preset === 'year-current') {
      return `Current Year (${format(start)} - ${format(end)})`;
    }
    if (range.preset === 'year-previous') {
      return `Previous Year (${format(start)} - ${format(end)})`;
    }
    return `${format(start)} - ${format(end)}`;
  };

  return (
    <div className="h-16 bg-transparent flex items-center justify-between pl-4 pr-6">
      {/* Left side - Decision Tracker or Greeting */}
      <div className="ml-4">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          {showGreeting ? (
            <Typography
              className="text-black font-medium"
              style={{
                fontSize: '24px',
                fontWeight: 500,
                lineHeight: '100%',
              }}
            >
              {greeting}, {userName}
            </Typography>
          ) : (
            <Typography
              className="text-black"
              style={{
                fontSize: '24px',
                fontStyle: 'normal',
                fontWeight: 'var(--font-weight-medium, 500)',
                lineHeight: '100%',
              }}
            >
              Decision Tracker
            </Typography>
          )}
        </motion.div>
      </div>

      {/* Right side - Date Picker and Notification */}
      <div className="flex items-center gap-2">
        {/* Date Picker */}
        {showPlaceholder && (
          <div className="relative" ref={datePickerRef}>
            <button
              onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
              className="flex items-center justify-between px-4 py-2 border border-slate-200 bg-white rounded-md text-sm hover:border-slate-300 transition-colors" 
              style={{ width: '280px', height: '40px' }}
            >
              <Typography className="text-slate-600 text-sm flex-1 text-left">
                {formatDateRange(dateRange)}
              </Typography>
              <CalendarTodayIcon sx={{ fontSize: 16, color: '#64748B' }} />
            </button>

            <DatePicker
              isOpen={isDatePickerOpen}
              onClose={() => setIsDatePickerOpen(false)}
              dateRange={dateRange}
              onApply={handleDateRangeApply}
            />
          </div>
        )}

        {/* Notification Icon */}
        <div className="relative" ref={notificationRef}>
          <button
            onClick={() => setIsNotificationOpen(!isNotificationOpen)}
            className="flex items-center justify-center w-9 h-9 rounded-sm bg-transparent hover:bg-slate-100"
          >
            <img
              src="/assets/icons/Bell.svg"
              alt="Notification Icon"
              className="w-5 h-5"
            />
            {notifications.some((n) => !n.isRead) && (
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
            )}
          </button>

          {isNotificationOpen && (
            <div className="absolute right-0 top-[calc(100%+4px)] w-96 bg-white rounded-md shadow-lg border border-slate-200 py-2 z-50">
              <div className="px-4 py-2 border-b border-slate-200">
                <Typography className="text-slate-900 font-medium text-sm">
                  Notifications
                </Typography>
              </div>
              <div className="max-h-40 overflow-y-auto">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className="flex items-center justify-between px-4 py-2 hover:bg-slate-50"
                  >
                    <Typography
                      className={`text-sm ${notification.isRead ? 'text-slate-500' : 'text-slate-700'}`}
                    >
                      {notification.message}
                    </Typography>
                    <Typography className="text-xs text-slate-500 ml-2 whitespace-nowrap">
                      {notification.time}
                    </Typography>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Topbar;